import { createSelector } from 'reselect';
import { flow } from 'fp-ts/function';
import { array, nonEmptyArray, option, string } from 'fp-ts';
import { composeProps, createPropsExtender } from '@mews/framework';
import { Reservation, ReservationTimeState, ResourceState, ServiceOrderState } from '@mews-commander/data-access';
import {
    ApplicationState,
    isBeforeCheckInState,
    isCanceledState,
    isConfirmedState,
    isDefiniteState,
    isProcessedState,
    isStartedState,
    isUnconfirmedState,
} from '@mews-commander/types';
import { compactArray, primitiveTypeUniq } from '@mews/fp';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { getResourcesByIds } from '@mews-commander/legacy-entities/src/ducks/resources';
import { getResourceCategoryName } from '@mews-commander/legacy-entities/src/ducks/resourceCategories';
import { getResourceCategoryIdByResourceId } from '@mews-commander/legacy-entities/src/ducks/resourceCategoryAssignments';
import { isReservationLockable } from '@mews-commander/legacy-entities/src/utils/reservations';
import { getServiceOrdersTimeUnitCount } from '@mews-commander/legacy-entities/src/selectors/orderItems';
import {
    getCheckedReservations,
    isSingleReservationChecked,
    getCheckedReservationsAvailabilityBlockIds,
    getCheckedReservationIds,
} from '.';

export const isStartable = ({ reservationTimeState }: Reservation) =>
    reservationTimeState === ReservationTimeState.ToBeCheckedIn ||
    reservationTimeState === ReservationTimeState.CheckInMissed;

const isProcessable = ({ reservationTimeState }: Reservation) =>
    reservationTimeState === ReservationTimeState.ToBeCheckedOut ||
    reservationTimeState === ReservationTimeState.CheckOutMissed;

export const isCuttable = ({ serviceOrderState, assignedResourceId }: Reservation) => {
    const isConfirmed = serviceOrderState === ServiceOrderState.Confirmed;
    const isStarted = serviceOrderState === ServiceOrderState.Started;
    const hasResourceAssigned = assignedResourceId !== null;
    return hasResourceAssigned && (isConfirmed || isStarted);
};

const isAssignable = ({ serviceOrderState }: Reservation) =>
    serviceOrderState === ServiceOrderState.Optional ||
    serviceOrderState === ServiceOrderState.Confirmed ||
    serviceOrderState === ServiceOrderState.Started ||
    serviceOrderState === ServiceOrderState.Processed;

type ReservationPredicate = (reservation: Reservation) => boolean;
const selectionIs = (predicate: ReservationPredicate) => flow(getCheckedReservations, array.every(predicate));
const selectionHas = (predicate: ReservationPredicate) => flow(getCheckedReservations, array.some(predicate));

export const selectionIsCheckinable = selectionIs(isStartable);
export const selectionIsCheckoutable = selectionIs(isProcessable);
export const selectionIsConfirmable = selectionIs(({ serviceOrderState }) => isUnconfirmedState(serviceOrderState));
export const selectionIsCancellable = selectionIs(
    ({ serviceOrderState }) => isUnconfirmedState(serviceOrderState) || isConfirmedState(serviceOrderState)
);
export const selectionIsDefinite = selectionIs(({ serviceOrderState }) => isDefiniteState(serviceOrderState));
export const selectionIsUnconfirmed = selectionIs(({ serviceOrderState }) => isUnconfirmedState(serviceOrderState));
export const selectionIsCanceled = selectionIs(({ serviceOrderState }) => isCanceledState(serviceOrderState));
export const selectionHasCanceled = selectionHas(({ serviceOrderState }) => isCanceledState(serviceOrderState));
export const selectionIsUnCheckInable = selectionIs(({ serviceOrderState }) => isStartedState(serviceOrderState));
export const selectionIsUnCheckOutable = selectionIs(({ serviceOrderState }) => isProcessedState(serviceOrderState));
export const selectionIsBeforeCheckIn = selectionIs(({ serviceOrderState }) => isBeforeCheckInState(serviceOrderState));

export const selectionIsMultipleNightsLong = createSelector(
    getCheckedReservationIds,
    getServiceOrdersTimeUnitCount,
    (reservationIds, serviceOrderTimeUnitCount) =>
        reservationIds.every((reservationId) => serviceOrderTimeUnitCount[reservationId] > 0)
);

export const selectionIsCheckedIn = selectionIs(({ serviceOrderState }) => isStartedState(serviceOrderState));
export const selectionHasBooker = selectionIs(({ bookerId }) => !!bookerId);

export const hasSelectedMultipleReservations = flow(getCheckedReservations, array.size, (count) => count > 1);
const getCheckedReservationsAssignedResourceIds = flow(
    getCheckedReservations,
    array.map((r) => r.assignedResourceId),
    compactArray
);
const resourcesExtender = createSelector(
    getCurrentEnterpriseId,
    getCheckedReservationsAssignedResourceIds,
    (enterpriseId: string, ids) => ({ enterpriseId, ids })
);
const getCheckedResourcesState = createSelector(
    composeProps(getResourcesByIds, resourcesExtender),
    flow(
        array.map((resource) => resource.state),
        primitiveTypeUniq
    )
);

const selectionIsInspected = flow(
    getCheckedResourcesState,
    array.every((state) => state === ResourceState.Inspected)
);
export const selectionIsCuttable = selectionIs(isCuttable);
export const selectionIsAssignable = selectionIs(isAssignable);
export const selectionIsInquired = selectionIs(
    ({ serviceOrderState }: Reservation) => serviceOrderState === ServiceOrderState.Inquired
);

export const getMinimalSelectedStartUtc = flow(
    getCheckedReservations,
    array.map((r) => r.startUtc),
    nonEmptyArray.fromArray,
    option.map(nonEmptyArray.min(string.Ord)),
    option.toUndefined
);

export const getMaximalSelectedEndUtc = flow(
    getCheckedReservations,
    array.map((r) => r.endUtc),
    nonEmptyArray.fromArray,
    option.map(nonEmptyArray.max(string.Ord)),
    option.toUndefined
);

export const singleSelectionIsCuttableAndCheckinableAndInspected = createSelector(
    isSingleReservationChecked,
    selectionIsCuttable,
    selectionIsCheckinable,
    selectionIsInspected,
    (singleReservationChecked, cuttable, checkinable, inspected) =>
        singleReservationChecked && cuttable && checkinable && inspected
);
export const getCheckedReservationsCommonAvailabilityBlockId = createSelector(
    getCheckedReservationsAvailabilityBlockIds,
    (ids) => (ids.length === 1 ? ids[0] : null)
);

type GetResourceCategoryName = (
    state: ApplicationState,
    props: { id: string | undefined; serviceId: string }
) => string;

export const getResourceCategoryNameByResourceId = composeProps(
    getResourceCategoryName as GetResourceCategoryName,
    createPropsExtender('id', getResourceCategoryIdByResourceId)
);

export const getLockableCheckedReservations = createSelector(
    getCheckedReservations,
    array.filter(isReservationLockable)
);
